import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "sr-only" }

import { computed, ref, onMounted, nextTick } from "vue";
import { gsap } from "gsap";

import { Bar } from "vue-chartjs";
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

import { average } from "~/js/utils/graphs";

import ListLegend from "~/vue/molecules/lists/ListLegend.vue";

import { getClasses } from "~/js/composables/classes";

interface Dataset {
	label: string;
	values: Array<Number>;
	color: string;
}

interface Props {
	animate?: boolean;
	dataset: Array<Dataset> | null;
	display?: "vertical" | "horizontal";
	legend: Array<string>;
	options?: any;
	showAverage?: boolean;
	utilities?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GraphBar',
  props: {
    animate: { type: Boolean, default: false },
    dataset: {},
    display: { default: "vertical" },
    legend: {},
    options: { default: null },
    showAverage: { type: Boolean, default: false },
    utilities: { default: "" }
  },
  setup(__props: any) {

// https://vue-chartjs.org/guide/
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, annotationPlugin);

const props = __props;

const classes = ref({
	wrapper: {
		aspect: "aspect-[2]",
		utilities: props.utilities,
	},
});
const wrapper = ref(null);
const chart = ref(null);

const chartData = computed(() => {
	const data = {
		labels: props.legend,
		datasets: props.dataset.map((set, i) => {
			return {
				borderRadius: 0, // 100000
				backgroundColor: set.color,
				barPercentage: 0.5,
				categoryPercentage: 0.9,
				maxBarThickness: 40,
				...set,
			};
		}),
	};

	return data;
});

const chartOptions = computed(() => {
	return {
		// animation: false,
		indexAxis: props.display == "horizontal" ? "y" : "x",
		plugins: {
			annotation: props.showAverage
				? {
					annotations: {
						line1: {
							type: "line",
							yMin: props.display !== "horizontal" ? average(props.dataset) : null,
							yMax: props.display !== "horizontal" ? average(props.dataset) : null,
							xMin: props.display === "horizontal" ? average(props.dataset) : null,
							xMax: props.display === "horizontal" ? average(props.dataset) : null,
							borderColor: "#ee730b",
							borderWidth: 3,
						},
					},
				}
				: {},
			backgroundBar: true,
			legend: {
				display: false,
			},
			axes: {
				labels: {
					// This more specific font property overrides the global property
					font: {
						family: "'Roboto', 'sans-serif'",
						size: 18,
					},
				},
			},
		},
		responsive: true,
		scales: {
			x: {
				stacked: true,
				grid: {
					display: false,
				},
				...props.options?.scales?.x,
			},
			y: {
				stacked: true,
				grid: {
					display: false,
				},
				...props.options?.scales?.y,
			},
		},
	};
});

const chartLegend = computed(() => {
	return props.dataset.map((set) => {
		return {
			label: set.label,
			color: set.color,
		};
	});
});

onMounted(() => {
	if (props.animate) {
		nextTick(() => {
			gsap.from(wrapper.value, {
				scrollTrigger: {
					trigger: wrapper.value,
					start: "-150% top",
					end: "0 top",
				},
				onStart: () => {
					const data = chart.value.chart.data.datasets.map((set) => set.data);
					chart.value.chart.data.datasets.forEach((_, i) => {
						chart.value.chart.data.datasets[i].data = null;
					});
					chart.value.chart.update();
					chart.value.chart.data.datasets.forEach((_, i) => {
						chart.value.chart.data.datasets[i].data = data[i];
					});
					chart.value.chart.update();
				},
				opacity: 0,
			});
		});
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("figure", {
    ref_key: "wrapper",
    ref: wrapper,
    class: _normalizeClass(_unref(getClasses)(classes.value, 'wrapper'))
  }, [
    _createVNode(_unref(Bar), {
      ref_key: "chart",
      ref: chart,
      data: chartData.value,
      options: chartOptions.value,
      class: "w-full"
    }, null, 8, ["data", "options"]),
    _createVNode(ListLegend, { legend: chartLegend.value }, null, 8, ["legend"]),
    _createElementVNode("table", _hoisted_1, [
      _createElementVNode("thead", null, [
        _createElementVNode("tr", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chartData.value.labels, (label) => {
            return (_openBlock(), _createElementBlock("td", { key: label }, _toDisplayString(label), 1))
          }), 128))
        ])
      ]),
      _createElementVNode("tbody", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chartData.value.datasets, (set) => {
          return (_openBlock(), _createElementBlock("tr", { key: set }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(set.data, (data) => {
              return (_openBlock(), _createElementBlock("td", { key: data }, _toDisplayString(data), 1))
            }), 128))
          ]))
        }), 128))
      ])
    ])
  ], 2))
}
}

})